import React, { useRef, useState } from "react";
import {
  Text,
  HStack,
  VStack,
  Box,
  Td,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Button,
  useToast,
  Icon,
} from "@chakra-ui/react";
import { v4 as uuidv4 } from "uuid";
import { fetchWrapper } from "../../helpers";
import { LuEye, LuEyeOff } from "react-icons/lu";

const KEY_TYPES = [
  { field: "api_key", label: "API Key" },
  { field: "sdk_key", label: "SDK Key" },
  { field: "story_key", label: "Story Key" },
  { field: "event_key", label: "Event Key" },
];

const KeysCell = ({ rs, loadList }) => {
  const [visibleKeys, setVisibleKeys] = useState({});
  const [confirmViewField, setConfirmViewField] = useState(null);
  const [confirmGenerateField, setConfirmGenerateField] = useState(null);
  const cancelRef = useRef();
  const toast = useToast();

  const labelFor = (field) => KEY_TYPES.find((t) => t.field === field)?.label;

  const generateKey = async (field) => {
    const data = { [field]: uuidv4(), id: rs.id };
    try {
      const url = `${process.env.REACT_APP_API_URL}/customer/update`;
      await fetchWrapper.post(url, data);
      toast({
        title: `${labelFor(field)} generated successfully.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      loadList && loadList();
    } catch (error) {
      toast({
        title: "Failed to generate key.",
        description: error.message || "Something went wrong.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleToggleVisibility = (field) => {
    if (visibleKeys[field]) {
      setVisibleKeys((prev) => ({ ...prev, [field]: false }));
    } else {
      setConfirmViewField(field);
    }
  };

  const handleConfirmGenerate = () => {
    if (confirmGenerateField) generateKey(confirmGenerateField);
    setConfirmGenerateField(null);
  };

  if (rs.status_id !== 1) {
    return (
      <Td>
        <Text color="gray.500" fontSize="13px">In Active User</Text>
      </Td>
    );
  }

  const setCount = KEY_TYPES.filter(({ field }) => rs[field] && rs[field].trim() !== "").length;

  return (
    <>
      <Td>
        <Accordion allowToggle>
          <AccordionItem border="none">
            <AccordionButton px={2} py={1} borderRadius="6px" _hover={{ bg: "gray.50" }}>
              <Box flex="1" textAlign="left" fontSize="12px" fontWeight="600" color="#374151">
                Keys <Text as="span" color="gray.400" fontWeight="400">({setCount}/{KEY_TYPES.length} set)</Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={2} pb={2} pt={1}>
              <VStack align="stretch" spacing={2}>
                {KEY_TYPES.map(({ field, label }) => {
                  const value = rs[field];
                  const isVisible = !!visibleKeys[field];
                  const hasValue = value && value.trim() !== "";

                  return (
                    <HStack key={field} justify="space-between" spacing={2}>
                      <Text fontSize="11px" fontWeight="600" color="#374151" minW="60px">
                        {label}
                      </Text>
                      {hasValue ? (
                        <HStack spacing={2} flex="1" justify="flex-end">
                          <Text fontSize="12px" color="#374151" noOfLines={1}>
                            {isVisible ? value : "*******"}
                          </Text>
                          <Icon
                            as={isVisible ? LuEyeOff : LuEye}
                            cursor="pointer"
                            onClick={() => handleToggleVisibility(field)}
                            fontSize="sm"
                          />
                          <Text
                            color="blue.500"
                            cursor="pointer"
                            fontSize="12px"
                            whiteSpace="nowrap"
                            onClick={() => setConfirmGenerateField(field)}
                          >
                            Re-Generate
                          </Text>
                        </HStack>
                      ) : (
                        <Text
                          color="blue.500"
                          cursor="pointer"
                          fontSize="12px"
                          onClick={() => setConfirmGenerateField(field)}
                        >
                          Generate
                        </Text>
                      )}
                    </HStack>
                  );
                })}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Td>

      {/* View confirmation — shared across all key types */}
      <AlertDialog
        isOpen={!!confirmViewField}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmViewField(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              View {labelFor(confirmViewField)}
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to view the {labelFor(confirmViewField)}? This may expose sensitive information.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmViewField(null)}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                ml={3}
                onClick={() => {
                  setVisibleKeys((prev) => ({ ...prev, [confirmViewField]: true }));
                  setConfirmViewField(null);
                }}
              >
                View
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Generate / Re-generate confirmation — shared across all key types */}
      <AlertDialog
        isOpen={!!confirmGenerateField}
        leastDestructiveRef={cancelRef}
        onClose={() => setConfirmGenerateField(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Key {rs[confirmGenerateField] ? "Re-generation" : "Generation"}
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to {rs[confirmGenerateField] ? "re-generate" : "generate"} the {labelFor(confirmGenerateField)}?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirmGenerateField(null)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleConfirmGenerate} ml={3}>
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default KeysCell;
